'use client';

import { useCallback, useRef, useState } from 'react';

import {
  AlertOctagon,
  AlertTriangle,
  ArrowLeftRight,
  ArrowRight,
  Check,
  ChevronUp,
  Copy,
  FileText,
  Quote,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { stripForDisplay } from '../chapter45';
import { getInteractionPairHeading } from '../lib/interaction-seo';
import type {
  ChplQuote,
  DrugInteraction,
  InteractionSeverity,
  ViewChplOptions,
} from '../types';
import { getSeverityConfig } from '../types';
import { formatSingleInteractionAsText } from '../utils/format-interactions-text';

interface InteractionCardProps {
  interaction: DrugInteraction;
  onViewChpl?: (quote: ChplQuote, options?: ViewChplOptions) => void;
  id?: string;
}

export function InteractionCard({
  interaction,
  onViewChpl,
  id,
}: InteractionCardProps) {
  const { t } = useTranslation('interactions');
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const config = getSeverityConfig(interaction.severity);

  const { truncated, wasTruncated } = truncateSummary(interaction.summary);
  // Inline reveal activates whenever there are ChPL quotes to expand to,
  // even if the summary itself is short enough to render in full.
  const hasRevealTarget = interaction.chplQuotes.length > 0;

  const handleCopy = useCallback(async () => {
    const text = formatSingleInteractionAsText(interaction, t);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  }, [interaction, t]);

  return (
    <article
      id={id}
      className={cn(
        'overflow-hidden rounded-lg border-2 transition-shadow',
        config.borderColor,
        config.bgColor,
        config.shadowColor,
      )}
    >
      {/* Header */}
      <div className="relative p-3 @[640px]:p-4">
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            'absolute top-4 right-2 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors',
            copied
              ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
              : cn(
                  'bg-white/40 dark:bg-zinc-800/40',
                  config.textColor,
                  'opacity-50 hover:opacity-80',
                ),
          )}
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              <span className="hidden @[640px]:inline">{t('copied')}</span>
            </>
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
        {/* Severity badge and icon */}
        <div className="mb-3 flex items-center gap-2 pr-8">
          <SeverityIcon severity={interaction.severity} />
          <span
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs font-bold tracking-wide uppercase',
              config.badgeColor,
            )}
          >
            {t(`severity.${interaction.severity}.label`)}
          </span>
          <span
            className={cn('hidden text-xs @[640px]:inline', config.textColor)}
          >
            {t(`severity.${interaction.severity}.description`)}
          </span>
        </div>

        {/* Drug pair names */}
        <h3
          className="flex flex-col gap-0.5 @[640px]:flex-row @[640px]:flex-wrap @[640px]:items-center @[640px]:gap-x-2 @[640px]:gap-y-0.5"
          aria-label={getInteractionPairHeading([
            interaction.pair.drug1,
            interaction.pair.drug2,
          ])}
        >
          <DrugName
            name={interaction.pair.drug1.productName}
            commonName={interaction.pair.drug1.commonName}
            severity={interaction.severity}
          />
          <span className="flex items-center gap-1.5">
            <ArrowLeftRight
              className={cn(
                'h-3.5 w-3.5 shrink-0 @[640px]:h-4 @[640px]:w-4',
                config.iconColor,
              )}
            />
            <DrugName
              name={interaction.pair.drug2.productName}
              commonName={interaction.pair.drug2.commonName}
              severity={interaction.severity}
            />
          </span>
        </h3>

        {/* Summary with inline reveal affordance */}
        <div className="mt-4">
          <p className={cn('text-sm leading-relaxed', config.textColor)}>
            {isExpanded || !wasTruncated ? (
              interaction.summary
            ) : (
              <>{truncated}…</>
            )}{' '}
            {!isExpanded && hasRevealTarget && (
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                aria-expanded={false}
                data-test="interaction-card-toggle-chpl-quotes"
                className={cn(
                  'font-semibold underline decoration-2 underline-offset-2',
                  config.linkColor,
                )}
              >
                {t('card.showMore')}
              </button>
            )}
          </p>
        </div>

        {/* Collapse affordance — standalone, centered, with chevron */}
        {isExpanded && hasRevealTarget && (
          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              aria-expanded={true}
              data-test="interaction-card-collapse"
              className={cn(
                'inline-flex items-center gap-1 text-sm font-semibold',
                config.linkColor,
              )}
            >
              <ChevronUp className="h-3.5 w-3.5" />
              {/*
                linkColor must be applied directly here too — text-decoration-color
                doesn't cascade from parent, so without it the underline would fall
                back to currentColor (the darker text-red-800) instead of red-400.
              */}
              <span
                className={cn(
                  'underline decoration-2 underline-offset-2',
                  config.linkColor,
                )}
              >
                {t('card.collapse')}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* ChPL Quotes Section — visible when expanded */}
      {isExpanded && interaction.chplQuotes.length > 0 && (
        <div
          className="border-t border-current/10 px-3 pt-3 pb-3 @[640px]:px-4 @[640px]:pt-4 @[640px]:pb-4"
          aria-hidden={false}
        >
          {interaction.chplQuotes.map((quote, index) => (
            <div key={index}>
              {index > 0 && (
                <hr
                  className={cn(
                    'my-5 border-t',
                    interaction.severity === 'critical' &&
                      'border-red-300 dark:border-red-700',
                    interaction.severity === 'important' &&
                      'border-orange-300 dark:border-orange-700',
                    interaction.severity === 'moderate' &&
                      'border-yellow-300 dark:border-yellow-700',
                    interaction.severity === 'minor' &&
                      'border-blue-300 dark:border-blue-700',
                  )}
                />
              )}

              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                <Quote
                  className={cn('h-3.5 w-3.5 shrink-0', config.iconColor)}
                />
                <span className="font-semibold">{quote.drugName}</span>
                {quote.activeSubstance && (
                  <span className="text-zinc-400 dark:text-zinc-500">
                    ({quote.activeSubstance})
                  </span>
                )}
              </div>

              <div className="space-y-3">
                {quote.texts.map((text, textIndex) => (
                  <blockquote
                    key={textIndex}
                    className={cn(
                      'rounded-xl border-l-4 bg-white/50 px-4 py-3 text-sm leading-relaxed italic @[640px]:px-6 @[640px]:py-5 dark:bg-zinc-900/40',
                      'text-zinc-600 dark:text-zinc-300',
                      interaction.severity === 'critical' && 'border-red-500',
                      interaction.severity === 'important' &&
                        'border-orange-500',
                      interaction.severity === 'moderate' &&
                        'border-yellow-500',
                      interaction.severity === 'minor' && 'border-blue-500',
                    )}
                  >
                    <ChplQuoteContent text={text} />
                  </blockquote>
                ))}
              </div>

              {quote.drugId && onViewChpl && (
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      onViewChpl(quote, { scrollToChapter45: true })
                    }
                    data-test="interaction-card-open-chpl-45"
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md border-2 px-3 py-1.5 text-xs font-medium transition-all',
                      'hover:translate-x-[1px] hover:translate-y-[1px]',
                      'active:translate-x-[1px] active:translate-y-[1px]',
                      interaction.severity === 'critical' &&
                        'border-red-400 bg-red-100 text-red-800 shadow-[2px_2px_0_0_#f87171] hover:bg-red-200 hover:shadow-[1px_1px_0_0_#f87171] active:shadow-[1px_1px_0_0_#f87171] dark:border-red-400 dark:bg-red-950 dark:text-red-300 dark:shadow-[2px_2px_0_0_#f87171] dark:hover:bg-red-900 dark:hover:shadow-[1px_1px_0_0_#f87171] dark:active:shadow-[1px_1px_0_0_#f87171]',
                      interaction.severity === 'important' &&
                        'border-orange-400 bg-orange-100 text-orange-800 shadow-[2px_2px_0_0_#fb923c] hover:bg-orange-200 hover:shadow-[1px_1px_0_0_#fb923c] active:shadow-[1px_1px_0_0_#fb923c] dark:border-orange-400 dark:bg-orange-950 dark:text-orange-300 dark:shadow-[2px_2px_0_0_#fb923c] dark:hover:bg-orange-900 dark:hover:shadow-[1px_1px_0_0_#fb923c] dark:active:shadow-[1px_1px_0_0_#fb923c]',
                      interaction.severity === 'moderate' &&
                        'border-yellow-400 bg-yellow-100 text-yellow-800 shadow-[2px_2px_0_0_#facc15] hover:bg-yellow-200 hover:shadow-[1px_1px_0_0_#facc15] active:shadow-[1px_1px_0_0_#facc15] dark:border-yellow-400 dark:bg-yellow-950 dark:text-yellow-300 dark:shadow-[2px_2px_0_0_#facc15] dark:hover:bg-yellow-900 dark:hover:shadow-[1px_1px_0_0_#facc15] dark:active:shadow-[1px_1px_0_0_#facc15]',
                      interaction.severity === 'minor' &&
                        'border-blue-400 bg-blue-100 text-blue-800 shadow-[2px_2px_0_0_#60a5fa] hover:bg-blue-200 hover:shadow-[1px_1px_0_0_#60a5fa] active:shadow-[1px_1px_0_0_#60a5fa] dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300 dark:shadow-[2px_2px_0_0_#60a5fa] dark:hover:bg-blue-900 dark:hover:shadow-[1px_1px_0_0_#60a5fa] dark:active:shadow-[1px_1px_0_0_#60a5fa]',
                    )}
                    title={t('card.openChapterTitle')}
                    aria-label={t('card.seeQuotesAria', {
                      count: quote.texts.length,
                      drugName: quote.drugName,
                    })}
                  >
                    <FileText className="h-3 w-3" />
                    <span>
                      <Trans
                        i18nKey="interactions:card.seeQuotesInChpl"
                        components={{ b: <span className="font-bold" /> }}
                      />
                    </span>
                    <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-current/15 px-1 text-[10px] font-bold tabular-nums">
                      {quote.texts.length}
                    </span>
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function DrugName({
  name,
  commonName,
  severity,
}: {
  name: string;
  commonName: string;
  severity: InteractionSeverity;
}) {
  const config = getSeverityConfig(severity);

  return (
    <span>
      <span className={cn('font-bold', config.textColor)}>{name}</span>
      {commonName && (
        <>
          {' '}
          <span
            className={cn(
              'inline-block text-xs whitespace-nowrap opacity-60 @[640px]:text-sm @[640px]:opacity-75',
              config.textColor,
            )}
          >
            ({commonName})
          </span>
        </>
      )}
    </span>
  );
}

function SeverityIcon({ severity }: { severity: InteractionSeverity }) {
  const config = getSeverityConfig(severity);

  if (severity === 'critical') {
    return <AlertOctagon className={cn('h-5 w-5', config.iconColor)} />;
  }

  return <AlertTriangle className={cn('h-5 w-5', config.iconColor)} />;
}

/**
 * Renders ChPL quote text as clean plain text.
 * Markdown/HTML formatting is stripped, displayed as a clean single line.
 */
function ChplQuoteContent({ text }: { text: string }) {
  // Strip markdown and HTML formatting, convert line breaks to spaces for cleaner display
  const cleanText = stripForDisplay(text, { preserveLineBreaks: false });

  return <div>{formatInlineText(cleanText)}</div>;
}

/**
 * Format inline text with [...] placeholders and <br> line breaks
 */
function formatInlineText(text: string): React.ReactNode {
  // First split by <br> tags to handle line breaks
  const lineSegments = text.split(/<br\s*\/?>/gi);

  return lineSegments.map((segment, lineIndex) => {
    // Handle [...] as ellipsis markers
    const ellipsisParts = segment.split(/(\[\.\.\.?\]|\(\.\.\.?\))/g);
    const segmentContent = ellipsisParts.map((part, i) => {
      if (
        part === '[...]' ||
        part === '[..]' ||
        part === '(...)' ||
        part === '(..)'
      ) {
        return (
          <span key={`${lineIndex}-${i}`} className="italic opacity-60">
            [...]
          </span>
        );
      }
      return <span key={`${lineIndex}-${i}`}>{part}</span>;
    });

    return (
      <span key={`line-${lineIndex}`}>
        {segmentContent}
        {lineIndex < lineSegments.length - 1 && <br />}
      </span>
    );
  });
}

/**
 * Snap-to-word truncation at ~200 chars. Strips trailing punctuation so the
 * ellipsis reads cleanly. Returns wasTruncated:false for short strings so the
 * caller can skip the inline reveal affordance.
 */
function truncateSummary(
  text: string,
  max = 200,
): { truncated: string; wasTruncated: boolean } {
  if (text.length <= max) return { truncated: text, wasTruncated: false };
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > max * 0.7 ? lastSpace : max;
  const trimmed = slice.slice(0, cut).replace(/[,;:.\s]+$/, '');
  return { truncated: trimmed, wasTruncated: true };
}
